import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api } from "@/lib/api";
import { listingCoords, COUNTRY_COORDS } from "@/lib/geo";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2, MapPin, Crown, BadgeCheck, LayoutGrid, Map as MapIcon, Navigation, X } from "lucide-react";
import { toast } from "sonner";
import { useUserLocation, listingDistanceFrom, formatDistance } from "@/hooks/useUserLocation";
import { getAllCitiesGlobal } from "@/data/cities";

// Build a tiny SVG marker tinted by listing type (avoids the broken default icon
// that ships with bundlers).
const buildIcon = ({ pro, badge, type }) => {
  const fill = pro ? "#D9923B" : badge ? "#C05A3C" : type === "service" ? "#2A3D36" : "#1A2024";
  const dot = pro ? "#1A2024" : "#FAF8F5";
  const svg = `
    <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 36 48' width='32' height='42'>
      <path d='M18 1 C9 1 2 7.5 2 17 C2 28 18 47 18 47 C18 47 34 28 34 17 C34 7.5 27 1 18 1 Z'
            fill='${fill}' stroke='#FAF8F5' stroke-width='1.5'/>
      <circle cx='18' cy='17' r='6' fill='${dot}'/>
    </svg>`;
  return L.divIcon({
    className: "afri-map-marker",
    html: svg,
    iconSize: [32, 42],
    iconAnchor: [16, 42],
    popupAnchor: [0, -36],
  });
};

const currencySymbol = (c) => ({ USD: "$", GBP: "£", EUR: "€", CAD: "C$", NGN: "₦" }[c] || `${c} `);

const FitBounds = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    if (points.length === 1) {
      map.setView(points[0], 9);
    } else {
      map.fitBounds(points, { padding: [40, 40], maxZoom: 7 });
    }
  }, [points, map]);
  return null;
};

const MapView = () => {
  const [params, setParams] = useSearchParams();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState({ countries: [] });
  const [showCityPins, setShowCityPins] = useState(true);
  const allCityPins = useMemo(() => getAllCitiesGlobal(), []);
  const cityIcon = useMemo(
    () => L.divIcon({
      className: "city-pin-marker",
      html: '<div style="background:#1A2024;color:#fff;border-radius:999px;font-size:10px;font-weight:600;padding:2px 8px;border:2px solid #C05A3C;white-space:nowrap;box-shadow:0 2px 4px rgba(0,0,0,0.3);">●</div>',
      iconSize: [16, 16], iconAnchor: [8, 8],
    }),
    [],
  );
  const mapRef = useRef(null);
  const { coords: userCoords, state: locState, request: requestLoc, clear: clearLoc } = useUserLocation();

  const type = params.get("type") || "";
  const country = params.get("country") || "";

  useEffect(() => { api.get("/meta").then((r) => setMeta(r.data)); }, []);

  useEffect(() => {
    setLoading(true);
    api.get("/listings", { params: { type: type || undefined, country: country || undefined, limit: 200 } })
      .then((r) => setListings(r.data))
      .finally(() => setLoading(false));
  }, [type, country]);

  const updateParam = (key, value) => {
    const p = new URLSearchParams(params);
    if (value && value !== "all") p.set(key, value); else p.delete(key);
    setParams(p);
  };

  const points = useMemo(() => listings
    .map((l) => ({ ...l, _coords: listingCoords(l) }))
    .filter((l) => l._coords), [listings]);

  const fitPoints = useMemo(() => points.map((p) => p._coords), [points]);
  const defaultCenter = country && COUNTRY_COORDS[country] ? COUNTRY_COORDS[country] : [20, 5];
  const defaultZoom = country ? 5 : 2;

  return (
    <div className="container-narrow py-10">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
        <div>
          <div className="label-overline text-[#C05A3C] mb-2">Map view</div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight">Sellers on the map</h1>
          <p className="text-muted-foreground mt-2 font-sans-ui">
            {points.length} of {listings.length} listing{listings.length === 1 ? "" : "s"} placed by region.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {userCoords ? (
            <Button data-testid="map-distance-off" variant="outline" onClick={() => { clearLoc(); toast.success("Distance hidden"); }} className="rounded-md">
              <X className="w-4 h-4 mr-2"/> Hide distance
            </Button>
          ) : (
            <Button data-testid="map-distance-on" variant="outline" onClick={() => requestLoc()} disabled={locState === "requesting"} className="rounded-md">
              {locState === "requesting" ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <Navigation className="w-4 h-4 mr-2"/>}
              Show distance
            </Button>
          )}
          <Button
            variant="outline"
            className="rounded-md"
            onClick={() => setShowCityPins((v) => !v)}
            data-testid="toggle-city-pins"
          >
            <MapPin className="w-4 h-4 mr-2" /> {showCityPins ? "Hide cities" : "Show cities"}
          </Button>
          <Link to="/browse" data-testid="map-to-grid-btn">
            <Button variant="outline" className="rounded-md"><LayoutGrid className="w-4 h-4 mr-2"/> Grid view</Button>
          </Link>
          <Button disabled className="bg-[#C05A3C] hover:bg-[#C05A3C] text-white rounded-md" data-testid="map-active-btn">
            <MapIcon className="w-4 h-4 mr-2"/> Map
          </Button>
        </div>
      </div>

      <div className="bg-white border border-black/10 rounded-lg p-5 mb-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="map-filters">
        <div>
          <label className="label-overline text-muted-foreground block mb-2">Type</label>
          <Select value={type || "all"} onValueChange={(v) => updateParam("type", v)}>
            <SelectTrigger data-testid="map-filter-type"><SelectValue placeholder="All"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="product">Products</SelectItem>
              <SelectItem value="service">Services</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="label-overline text-muted-foreground block mb-2">Country</label>
          <Select value={country || "all"} onValueChange={(v) => updateParam("country", v)}>
            <SelectTrigger data-testid="map-filter-country"><SelectValue placeholder="Any"/></SelectTrigger>
            <SelectContent className="max-h-80">
              <SelectItem value="all">Any</SelectItem>
              {meta.countries?.map((c) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="lg:col-span-2 flex items-end gap-2 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-[#1A2024]"/> Product</span>
          <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-[#2A3D36]"/> Service</span>
          <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-[#C05A3C]"/> Verified</span>
          <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-[#D9923B]"/> Pro</span>
        </div>
      </div>

      <div className="relative bg-white border border-black/10 rounded-lg overflow-hidden" data-testid="map-container" style={{ height: "70vh" }}>
        {loading && (
          <div className="absolute inset-0 z-[500] flex items-center justify-center bg-white/70 backdrop-blur-sm">
            <Loader2 className="w-6 h-6 animate-spin text-[#C05A3C]"/>
          </div>
        )}
        <MapContainer
          ref={mapRef}
          center={defaultCenter}
          zoom={defaultZoom}
          scrollWheelZoom
          style={{ height: "100%", width: "100%" }}
          worldCopyJump
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {showCityPins && allCityPins.map((c) => (
            <Marker key={`city-${c.countrySlug}-${c.citySlug}`} position={[c.lat, c.lng]} icon={cityIcon}>
              <Popup>
                <div className="w-44" data-testid={`city-popup-${c.countrySlug}-${c.citySlug}`}>
                  <div className="font-display text-sm font-semibold mb-1">{c.name}</div>
                  <Link to={`/${c.countrySlug}/${c.citySlug}`} className="text-xs text-[#C05A3C] font-semibold underline">
                    African sellers in {c.name} →
                  </Link>
                </div>
              </Popup>
            </Marker>
          ))}
          {points.map((l) => (
            <Marker
              key={l.listing_id}
              position={l._coords}
              icon={buildIcon({ pro: l.owner_is_pro, badge: l.owner_verified_badge, type: l.type })}
            >
              <Popup>
                <div className="w-56" data-testid={`map-popup-${l.listing_id}`}>
                  {l.images?.[0] && (
                    <img src={l.images[0]} alt={l.title} className="w-full h-24 object-cover rounded-md mb-2"/>
                  )}
                  <div className="flex items-start gap-1 mb-1">
                    <h4 className="font-display text-base leading-tight flex-1">{l.title}</h4>
                  </div>
                  <div className="flex items-center gap-1 mb-1">
                    {l.owner_is_pro && <Badge className="bg-[#D9923B] text-[#1A2024] text-[10px]"><Crown className="w-2.5 h-2.5 mr-0.5"/> PRO</Badge>}
                    {!l.owner_is_pro && l.owner_verified_badge && <Badge className="bg-[#C05A3C] text-white text-[10px]"><BadgeCheck className="w-2.5 h-2.5 mr-0.5"/> Verified</Badge>}
                    <Badge variant="outline" className="text-[10px] capitalize">{l.type}</Badge>
                  </div>
                  <div className="text-xs flex items-center gap-1 text-muted-foreground mb-1">
                    <MapPin className="w-3 h-3"/> {l.city ? `${l.city}, ` : ""}{l.country}
                  </div>
                  {userCoords && (
                    <div className="text-xs flex items-center gap-1 text-[#C05A3C] font-semibold mb-1" data-testid={`map-distance-${l.listing_id}`}>
                      <Navigation className="w-3 h-3"/> {formatDistance(listingDistanceFrom(l, userCoords)) || "—"}
                    </div>
                  )}
                  <div className="font-semibold text-sm mb-2">
                    {currencySymbol(l.currency)}{l.price}{l.type === "service" && <span className="text-xs text-muted-foreground">/hr</span>}
                  </div>
                  <Link to={`/listing/${l.listing_id}`} className="text-xs text-[#C05A3C] font-semibold underline">View listing →</Link>
                </div>
              </Popup>
            </Marker>
          ))}
          {fitPoints.length > 0 && <FitBounds points={fitPoints}/>}
        </MapContainer>
      </div>

      {!loading && points.length === 0 && (
        <div className="py-10 text-center text-muted-foreground">
          No listings to place on the map. Try removing filters.
        </div>
      )}
    </div>
  );
};

export default MapView;
